import { Component } from '@theme/component';

/**
 * Product gallery: one large frame, a single row of thumbnails, arrows, and a
 * lightbox.
 *
 * Ported from components/ProductGallery.js. Swapping the main image rather
 * than scrolling a stack is what lets the frame stay big — gate photography is
 * landscape and the subject fills it.
 *
 * Everything routes through #ga(index) so the stage, the thumbnail strip and
 * the lightbox can never disagree about which item is showing. The arrows and
 * the thumbnails are two ways of moving the same cursor.
 *
 * @extends {Component}
 */
class AwhGallery extends Component {
  /** Index into `#thumbs`. The stage always shows this one. */
  #i = 0;

  /** @type {ResizeObserver | undefined} */
  #ro;

  get #thumbs() {
    return [...this.querySelectorAll('[data-thumb]')];
  }

  scrollBack = () => this.#page(-1);

  scrollOn = () => this.#page(1);

  /** Just short of a full width, so one column stays on screen as an anchor. */
  #page(dir) {
    const strip = this.querySelector('[ref="thumbs"]');
    if (!(strip instanceof HTMLElement)) return;
    strip.scrollBy({ left: dir * Math.round(strip.clientWidth * 0.8), behavior: 'smooth' });
  }

  /**
   * Which way there is more to see. Drives the edge fades and disables the
   * controls at each end — a scroll button that does nothing when pressed is
   * worse than no button.
   */
  #edges = () => {
    const strip = this.querySelector('[ref="thumbs"]');
    if (!(strip instanceof HTMLElement)) return;

    const max = strip.scrollWidth - strip.clientWidth;
    const x = strip.scrollLeft;
    const start = x <= 1;
    const end = x >= max - 1;

    strip.dataset.rand = max <= 1 ? 'geen' : start ? 'verder' : end ? 'terug' : 'beide';

    const back = this.querySelector('[ref="stripterug"]');
    const on = this.querySelector('[ref="stripverder"]');
    if (back instanceof HTMLButtonElement) back.disabled = max <= 1 || start;
    if (on instanceof HTMLButtonElement) on.disabled = max <= 1 || end;

    const wrap = this.querySelector('[ref="strip"]');
    if (wrap instanceof HTMLElement) wrap.dataset.scrollbaar = max <= 1 ? 'nee' : 'ja';
  };

  /**
   * Roving tabindex, which role="tablist" asks for: one stop in the tab order,
   * then Left/Right to walk the thumbnails. Without it, twelve photographs
   * meant twelve tab stops between the gallery and the configurator.
   */
  #roving() {
    const thumbs = this.#thumbs;
    for (const [n, t] of thumbs.entries()) t.tabIndex = n === this.#i ? 0 : -1;
  }

  #onStripKey = (event) => {
    const map = { ArrowRight: 1, ArrowLeft: -1 };
    const by = map[event.key];
    if (by) {
      event.preventDefault();
      this.#step(by);
      this.#thumbs[this.#i]?.focus();
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.#ga(event.key === 'Home' ? 0 : this.#thumbs.length - 1);
      this.#thumbs[this.#i]?.focus();
    }
  };

  /**
   * Scroll the strip so the chosen thumbnail is in view *with a neighbour
   * beside it*. Plain scrollIntoView does nothing once an item is fully
   * visible, so choosing the last one on screen left no sign that more
   * existed — which is what AW Hekken ran into. One column of look-ahead is
   * the smallest amount that always reveals the next.
   */
  #reveal(btn) {
    const strip = this.querySelector('[ref="thumbs"]');
    if (!(strip instanceof HTMLElement) || !(btn instanceof HTMLElement)) return;
    if (strip.scrollWidth <= strip.clientWidth) return;

    const gap = Number.parseFloat(getComputedStyle(strip).columnGap) || 0;
    const peek = btn.offsetWidth + gap;
    const left = btn.offsetLeft - peek;
    const right = btn.offsetLeft + btn.offsetWidth + peek;

    if (right > strip.scrollLeft + strip.clientWidth) {
      strip.scrollTo({ left: right - strip.clientWidth, behavior: 'smooth' });
    } else if (left < strip.scrollLeft) {
      strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
  }

  connectedCallback() {
    super.connectedCallback();

    const strip = this.querySelector('[ref="thumbs"]');
    if (strip) {
      strip.addEventListener('scroll', this.#edges, { passive: true });
      strip.addEventListener('keydown', this.#onStripKey);
      // The strip's width depends on the column beside it, so the overflow
      // state has to be recomputed on layout rather than once on load.
      this.#ro = new ResizeObserver(this.#edges);
      this.#ro.observe(strip);
      this.#roving();
      this.#edges();
    }

    const dialog = this.querySelector('[ref="dialog"]');
    if (dialog instanceof HTMLDialogElement) {
      // Arrow keys inside the lightbox. Escape is the browser's job and is
      // deliberately not handled here — `cancel` restores the scroller.
      dialog.addEventListener('keydown', this.#onKey);
      // A click that lands on the dialog itself rather than on the panel is a
      // click on the backdrop. `showModal` gives the backdrop no element of
      // its own, so this is the way to catch it.
      dialog.addEventListener('click', this.#onBackdrop);
      dialog.addEventListener('close', this.#release);
      dialog.addEventListener('cancel', this.#release);
    }
  }

  disconnectedCallback() {
    this.#ro?.disconnect();
    const strip = this.querySelector('[ref="thumbs"]');
    if (strip) {
      strip.removeEventListener('scroll', this.#edges);
      strip.removeEventListener('keydown', this.#onStripKey);
    }

    const dialog = this.querySelector('[ref="dialog"]');
    if (dialog instanceof HTMLDialogElement) {
      dialog.removeEventListener('keydown', this.#onKey);
      dialog.removeEventListener('click', this.#onBackdrop);
      dialog.removeEventListener('close', this.#release);
      dialog.removeEventListener('cancel', this.#release);
    }
    this.#release();
    super.disconnectedCallback();
  }

  /** `event.target`, not `currentTarget` — component.js delegates on document. */
  select = (event) => {
    const btn = event.target?.closest?.('[data-thumb]');
    if (!btn) return;
    this.#ga(this.#thumbs.indexOf(btn));
  };

  next = () => this.#step(1);

  prev = () => this.#step(-1);

  /**
   * Wraps at both ends. A gallery is a ring, not a list with a dead end: a
   * visitor who arrows past the last photo means "keep going", and disabling
   * the control there just makes them hunt for the first thumbnail.
   */
  #step(by) {
    const n = this.#thumbs.length;
    if (n < 2) return;
    this.#ga((this.#i + by + n) % n);
  }

  open = () => {
    const dialog = this.querySelector('[ref="dialog"]');
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    if (this.#thumbs[this.#i]?.getAttribute('data-kind') === 'video') return;

    this.#paint();
    dialog.showModal();

    // showModal makes the background inert but does not stop it scrolling, and
    // Horizon scrolls .page-wrapper rather than the document — so the page
    // would slide behind the lightbox on a trackpad. Pinned here, restored on
    // close.
    const scroller = document.querySelector('.page-wrapper');
    if (scroller instanceof HTMLElement) {
      scroller.dataset.awhScrollLock = scroller.style.overflow || '';
      scroller.style.overflow = 'hidden';
    }
  };

  close = () => {
    const dialog = this.querySelector('[ref="dialog"]');
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
  };

  #release = () => {
    const scroller = document.querySelector('.page-wrapper');
    if (!(scroller instanceof HTMLElement)) return;
    if (!('awhScrollLock' in scroller.dataset)) return;
    scroller.style.overflow = scroller.dataset.awhScrollLock;
    delete scroller.dataset.awhScrollLock;
  };

  #onKey = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  };

  /**
   * Click-outside-to-close. The panel fills the dialog, so a click almost
   * never lands on the dialog element itself — testing only for that made the
   * gesture dead. Anything that is not the photograph or a control counts as
   * outside.
   */
  #onBackdrop = (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;
    if (t.closest('.galerij__licht-foto, button')) return;
    this.close();
  };

  /** Move the cursor and bring the stage, the strip and the lightbox in line. */
  #ga(index) {
    const thumbs = this.#thumbs;
    const btn = thumbs[index];
    if (!btn) return;
    this.#i = index;

    const stage = this.querySelector('[ref="main"]');
    // `.galerij__foto`, not a data attribute: `image_tag` takes named
    // arguments, and Liquid will not parse a hyphen in one, so `data-photo: ''`
    // was silently dropped and the swap had nothing to write to.
    const photo = this.querySelector('.galerij__foto');
    const zoom = this.querySelector('[ref="zoom"]');
    const tag = this.querySelector('[data-tag]');
    const clips = this.querySelectorAll('[data-video]');
    const isVideo = btn.getAttribute('data-kind') === 'video';

    // Leaving a clip stops it. Without this, switching thumbnails left the
    // previous video playing behind the photograph.
    for (const v of this.querySelectorAll('[data-video] video')) v.pause();

    if (isVideo) {
      const i = btn.getAttribute('data-video-index');
      for (const c of clips) c.hidden = c.getAttribute('data-video') !== i;
      if (zoom) zoom.hidden = true;
      if (tag) tag.hidden = true;
      stage?.classList.add('galerij__hoofd--video');
    } else {
      for (const c of clips) c.hidden = true;
      stage?.classList.remove('galerij__hoofd--video');
      const full = btn.getAttribute('data-full');
      if (photo instanceof HTMLImageElement && full) {
        photo.removeAttribute('srcset');
        photo.src = full;
        photo.alt = btn.getAttribute('data-alt') || photo.alt;
      }
      if (zoom) {
        zoom.hidden = false;
        zoom.setAttribute('data-zoom-src', btn.getAttribute('data-zoom-src') || '');
      }
      if (tag) tag.hidden = false;
    }

    for (const t of thumbs) {
      const on = t === btn;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', String(on));
    }

    this.#roving();
    this.#reveal(btn);

    const dialog = this.querySelector('[ref="dialog"]');
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      if (isVideo) this.close();
      else this.#paint();
    }
  }

  /** Write the current photograph into the lightbox. */
  #paint() {
    const btn = this.#thumbs[this.#i];
    const img = this.querySelector('[ref="lightfoto"]');
    const teller = this.querySelector('[ref="teller"]');
    const zoom = this.querySelector('[ref="zoom"]');

    const src =
      btn?.getAttribute('data-zoom-src') || zoom?.getAttribute('data-zoom-src') || '';
    if (img instanceof HTMLImageElement && src) {
      img.src = src;
      img.alt = btn?.getAttribute('data-alt') || '';
    }

    if (teller) {
      const photos = this.#thumbs.filter((t) => t.getAttribute('data-kind') !== 'video');
      const at = photos.indexOf(btn);
      teller.textContent = at < 0 ? '' : `${at + 1} / ${photos.length}`;
    }
  }
}

if (!customElements.get('awh-gallery-component')) {
  customElements.define('awh-gallery-component', AwhGallery);
}
